import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaChevronRight, FaEye, FaExternalLinkAlt, FaCrown } from 'react-icons/fa';
import './scss/teamSelectionPage.scss';
import Header from './Header';
import logo from './images/logo-long.svg';
import api from './services/apiService';
import { authService } from './services/authService';
import { useNotification } from './components/NotificationProvider';
// import clarityService from './services/clarityService'; // Disabled - tracking removed for App Store compliance

function TeamSelectionPage() {
  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [children, setChildren] = useState([]);
  const [parentName, setParentName] = useState('');
  const [showGdprConsent, setShowGdprConsent] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);
  const [isSubmittingConsent, setIsSubmittingConsent] = useState(false);
  const [isVersionUpdate, setIsVersionUpdate] = useState(false);
  const [teamProStatus, setTeamProStatus] = useState({});
  // Current version of Terms and Privacy Policy - update this when terms change
  const CURRENT_VERSION = "1.0";
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const userId = sessionStorage.getItem('user_id') || sessionStorage.getItem('player_id');
  const userType = sessionStorage.getItem('user_type') || 'player';
  const isParent = userType === 'parent';
  const isSuperAdmin = authService.isSuperAdmin();

  // Add logging for component mount/unmount
  useEffect(() => {
    // Get parent name if user is a parent
    if (isParent) {
      const firstName = sessionStorage.getItem('first_name') || '';
      if (firstName) {
        // Capitalize the first name and don't use surname
        setParentName(firstName.charAt(0).toUpperCase() + firstName.slice(1));
      }
    }
    
    // Check if GDPR consent is needed for all user types
    checkGdprConsent();
  }, []);
  
  // Function to check if GDPR consent is needed
  const checkGdprConsent = async () => {
    if (!userId) return;
    
    try {
      let endpoint;
      if (isParent) {
        endpoint = `${process.env.REACT_APP_API_URL}/check_gdpr_consent_simple.php?parent_id=${userId}`;
      } else {
        endpoint = `${process.env.REACT_APP_API_URL}/check_manager_gdpr_consent_simple.php?manager_id=${userId}`;
      }
      
      const response = await api.get(endpoint);
      
      if (response.data && response.data.success) {
        const userVersion = response.data.consent_version;
        const consentDate = response.data.consent_date;
        
        // Check if consent is needed (no consent date or outdated version)
        if (!consentDate || userVersion !== CURRENT_VERSION) {
          // Check if this is a version update or first-time consent
          if (consentDate && userVersion && userVersion !== CURRENT_VERSION) {
            setIsVersionUpdate(true);
          } else {
            setIsVersionUpdate(false);
          }
          
          setShowGdprConsent(true);
        }
      }
    } catch (error) {
      console.error('Error checking GDPR consent:', error);
    }
  };
  
  // Function to handle GDPR consent submission
  const handleGdprConsent = async () => {
    if (!gdprChecked) {
      showError('You must agree to the terms and conditions and privacy policy to continue.');
      return;
    }
    
    setIsSubmittingConsent(true);
    
    try {
      let endpoint;
      let data;
      
      if (isParent) {
        endpoint = `${process.env.REACT_APP_API_URL}/update_gdpr_consent_simple.php`;
        data = {
          parent_id: userId,
          consent_given: true,
          version: CURRENT_VERSION
        };
      } else {
        endpoint = `${process.env.REACT_APP_API_URL}/update_manager_gdpr_consent_simple.php`;
        data = {
          manager_id: userId,
          consent_given: true,
          version: CURRENT_VERSION
        };
      }
      
      const response = await api.post(endpoint, data);
      
      if (response.data && response.data.success) {
        setShowGdprConsent(false);
        
        if (isVersionUpdate) {
          showSuccess('Thank you for agreeing to our updated terms and conditions.');
        } else {
          showSuccess('Thank you for agreeing to our terms and conditions.');
        }
      } else {
        showError('There was an error saving your consent. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting GDPR consent:', error);
      showError('There was an error saving your consent. Please try again.');
    } finally {
      setIsSubmittingConsent(false);
    }
  };

  // Function to check Pro status for each team
  const checkTeamProStatus = async (teamIds) => {
    if (!teamIds || teamIds.length === 0) return;
    
    const proStatus = {};
    
    for (const teamId of teamIds) {
      try {
        const token = sessionStorage.getItem('token');
        const url = `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`;
        
        const response = await api.get(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.success) {
          proStatus[teamId] = response.data.is_pro === true;
        } else {
          proStatus[teamId] = false;
        }
      } catch (error) {
        console.error(`Error checking Pro status for team ${teamId}:`, error);
        proStatus[teamId] = false;
      }
    }
    
    setTeamProStatus(proStatus);
  };

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }

    const fetchTeams = async () => {
      try {
        const results = await api.get(`${process.env.REACT_APP_API_URL}/fetch_teams.php?user_id=${userId}&user_type=${userType}`);
        const result = results.data;

        if (result.success) {
          // Store children data if user is a parent
          if (isParent && result.children) {
            setChildren(result.children);
          }
          
          // No automatic redirection, always show team selection screen
          setTeams(result.teams || []);
          
          // Check Pro status for all teams
          const teamIds = result.teams?.map(team => team.team_id) || [];
          if (teamIds.length > 0) {
            checkTeamProStatus(teamIds);
          }
        } else {
          console.error('Failed to fetch teams');
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();

    // If superadmin, fetch all teams
    if (isSuperAdmin) {
      const fetchAllTeams = async () => {
        try {
          const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_all_teams.php`);
          if (response.data.success) {
            setAllTeams(response.data.teams || []);
            
            // Check Pro status for all teams (superadmin can view all teams)
            const allTeamIds = response.data.teams?.map(team => team.team_id) || [];
            if (allTeamIds.length > 0) {
              checkTeamProStatus(allTeamIds);
            }
          } else {
            console.error('Failed to fetch all teams');
          }
        } catch (error) {
          console.error('Error fetching all teams:', error);
        }
      };
      
      fetchAllTeams();
    }
  }, [userId, userType, isParent, location.pathname, navigate, isSuperAdmin]);

  const handleTeamSwitch = (teamId, teamName, teamType, childId = null, childName = null) => {
    // Check if we have all required info
    if (!teamId) {
      console.error("Missing teamId!", { teamId, teamName });
      return;
    }
    
    sessionStorage.setItem('selected_team_id', teamId);
    sessionStorage.setItem('selected_team_name', teamName);
    sessionStorage.setItem('selected_team_type', teamType || '');
    
    // Clarity tracking disabled - removed for App Store compliance
    // clarityService.trackEvent('team_selected', {
    //   teamId: teamId,
    //   teamName: teamName,
    //   teamType: teamType,
    //   userType: userType,
    //   childId: childId,
    //   childName: childName
    // });
    // clarityService.updateUserData();
    
    // Store team configuration if available
    const selectedTeam = teams.find(team => team.team_id === teamId) || 
                         allTeams.find(team => team.team_id === teamId);
    if (selectedTeam) {
      sessionStorage.setItem('selected_team_has_waitinglist', selectedTeam.has_waitinglist || 'false');
      sessionStorage.setItem('selected_team_has_max_players', selectedTeam.has_max_players || 'false');
      sessionStorage.setItem('selected_team_has_training', selectedTeam.has_training ? 'true' : 'false');
      
      // Store age_group if available
      if (selectedTeam.age_group) {
        sessionStorage.setItem('selected_team_age_group', selectedTeam.age_group);
      } else {
        sessionStorage.removeItem('selected_team_age_group');
      }
      
      // Store league_name if available
      if (selectedTeam.league_name) {
        sessionStorage.setItem('selected_team_league', selectedTeam.league_name);
      } else {
        sessionStorage.removeItem('selected_team_league');
      }
      
      // Store permanent keeper if available
      if (selectedTeam.permanent_keeper) {
        sessionStorage.setItem('permanent_keeper', selectedTeam.permanent_keeper);
      } else {
        sessionStorage.removeItem('permanent_keeper');
      }
    }
    
    // If a child was selected and we're a parent, store the player_id
    if (isParent) {
      if (childId) {
        sessionStorage.setItem('player_id', childId);
        
        // If we have the child's name, store it too
        if (childName) {
          sessionStorage.setItem('child_name', childName);
        }
      } else {
        // Try to find a child for this team
        const childrenForTeam = getChildrenForTeam(teamId);
        if (childrenForTeam.length > 0) {
          const firstChild = childrenForTeam[0];
          sessionStorage.setItem('player_id', firstChild.id);
          sessionStorage.setItem('child_name', firstChild.name);
        }
      }
    }
    
    navigate('/dashboard');
  };

  // View team as a player for superadmin
  const handleViewAsPlayer = (teamId, teamName, teamType, adminPlayerId) => {
    if (!teamId) {
      console.error("Missing teamId!", { teamId, teamName });
      return;
    }
    
    // Store team info
    sessionStorage.setItem('selected_team_id', teamId);
    sessionStorage.setItem('selected_team_name', teamName);
    sessionStorage.setItem('selected_team_type', teamType || '');
    
    // Set viewing mode for superadmin
    sessionStorage.setItem('superadmin_viewing_as_player', 'true');
    
    // Store admin's player ID to access fixtures
    if (adminPlayerId) {
      sessionStorage.setItem('player_id', adminPlayerId);
      console.log("Setting admin player_id for fixtures:", adminPlayerId);
    }
    
    // Get team configuration
    const selectedTeam = allTeams.find(team => team.team_id === teamId);
    if (selectedTeam) {
      sessionStorage.setItem('selected_team_has_waitinglist', selectedTeam.has_waitinglist || 'false');
      sessionStorage.setItem('selected_team_has_max_players', selectedTeam.has_max_players || 'false');
      sessionStorage.setItem('selected_team_has_training', selectedTeam.has_training ? 'true' : 'false');
      
      // Store age_group if available
      if (selectedTeam.age_group) {
        sessionStorage.setItem('selected_team_age_group', selectedTeam.age_group);
      } else {
        sessionStorage.removeItem('selected_team_age_group');
      }
      
      // Store league_name if available
      if (selectedTeam.league_name) {
        sessionStorage.setItem('selected_team_league', selectedTeam.league_name);
      } else {
        sessionStorage.removeItem('selected_team_league');
      }
      
      // Store permanent keeper if available
      if (selectedTeam.permanent_keeper) {
        sessionStorage.setItem('permanent_keeper', selectedTeam.permanent_keeper);
      } else {
        sessionStorage.removeItem('permanent_keeper');
      }
    }
    
    navigate('/dashboard');
  };

  // Instead of hardcoded values, use the actual children data
  const getChildrenForTeam = (teamId) => {
    if (!isParent || !children || children.length === 0) {
      return [];
    }
    
    // Ensure string comparison
    const targetTeamId = String(teamId);
    
    const childrenWithTeam = children.filter(child => {
      // First check the primary team_id
      if (String(child.team_id) === targetTeamId) {
        return true;
      }
      
      // Then check all_team_ids if present
      if (child.all_team_ids && Array.isArray(child.all_team_ids)) {
        return child.all_team_ids.some(tid => String(tid) === targetTeamId);
      }
      
      return false;
    });
    
    return childrenWithTeam.map(child => ({
      name: `${child.first_name} ${child.last_name}`,
      id: child.player_id
    }));
  };

  // The custom header component with centered logo
  const CustomHeader = () => (
    <header className="header">
      <div className="logo-container">
        <img 
          src={logo} 
          alt="Logo" 
          className="logo centered-logo" 
          onClick={() => navigate('/dashboard')} 
        />
      </div>
    </header>
  );

  // Create team-child combinations for display
  const teamChildCombos = [];
  
  // First, process accepted teams
  teams.filter(team => team.status !== 'pending').forEach(team => {
    const childrenForTeam = getChildrenForTeam(team.team_id);
    
    if (childrenForTeam.length > 0) {
      // Create an entry for each child on this team
      childrenForTeam.forEach(child => {
      teamChildCombos.push({
        teamId: team.team_id,
        teamName: team.team_name,
        teamType: team.type,
        childId: child.id,
        childName: child.name,
        location: team.location,
        pitch: team.pitch,
        county: team.county,
        has_waitinglist: team.has_waitinglist,
        has_max_players: team.has_max_players,
        has_training: team.has_training,
        age_group: team.age_group
      });
      });
    } else if (isParent) {
      // For parent users, if no children found for this team, don't add it to the list
    } else {
      // For non-parent users, still add the team without child info
      teamChildCombos.push({
        teamId: team.team_id,
        teamName: team.team_name,
        teamType: team.type,
        location: team.location,
        pitch: team.pitch,
        county: team.county,
        has_waitinglist: team.has_waitinglist,
        has_max_players: team.has_max_players,
        has_training: team.has_training,
        age_group: team.age_group
      });
    }
  });
  
  // Look for any multi-team children that might have been missed
  if (isParent && children && children.length > 0) {
    children.forEach(child => {
      if (child.all_team_ids && Array.isArray(child.all_team_ids) && child.all_team_ids.length > 1) {
        // For each team ID this child belongs to
        child.all_team_ids.forEach(teamId => {
          // Find this team in our teams list
          const team = teams.find(t => String(t.team_id) === String(teamId));
          if (team) {
            // Check if this combination already exists
            const exists = teamChildCombos.some(combo => 
              String(combo.teamId) === String(teamId) && 
              String(combo.childId) === String(child.player_id)
            );
            
            if (!exists) {
              teamChildCombos.push({
                teamId: team.team_id,
                teamName: team.team_name,
                teamType: team.type,
                childId: child.player_id,
                childName: `${child.first_name} ${child.last_name}`,
                location: team.location,
                pitch: team.pitch,
                county: team.county,
                has_waitinglist: team.has_waitinglist,
                has_max_players: team.has_max_players,
                has_training: team.has_training,
                age_group: team.age_group
              });
            }
          }
        });
      }
    });
  }

  // If we have no teams at all and not a superadmin, show "no teams" message instead of blank screen
  if (teams.length === 0 && !isSuperAdmin) {
    return (
      <div className="TeamSelectionPage container">
        <CustomHeader />
        {isParent && parentName && (
          <div className="parent-welcome">
            Welcome, {parentName}
          </div>
        )}
        <h1>{isParent ? "Select Your Child's Team" : "Select Your Team"}</h1>
        <div className="no-teams-message">
          No teams are currently available. Please contact support if you believe this is an error.
        </div>
      </div>
    );
  }

  return (
    <div className="TeamSelectionPage container">
      <CustomHeader />
      {isParent && parentName && (
        <div className="parent-welcome">
          Welcome, {parentName}
        </div>
      )}
      
      {/* GDPR Consent Modal */}
      {showGdprConsent && (
        <div className="gdpr-consent-overlay">
          <div className="gdpr-consent-modal">
            <h2>Terms and Conditions & Privacy Policy</h2>
            <p>
              {isVersionUpdate 
                ? 'Our Terms and Conditions or Privacy Policy have been updated. Please review and agree to the latest version to continue.'
                : 'Before you continue, we need your consent to our Terms and Conditions and Privacy Policy.'}
            </p>
            
            <div className="consent-links">
              <a href="https://firstwhistle.app/terms/" target="_blank" rel="noopener noreferrer">
                Terms and Conditions <FaExternalLinkAlt size={12} />
              </a>
              <a href="https://firstwhistle.app/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy <FaExternalLinkAlt size={12} />
              </a>
            </div>
            
            <div className="consent-checkbox">
              <label>
                <input 
                  type="checkbox" 
                  checked={gdprChecked} 
                  onChange={(e) => setGdprChecked(e.target.checked)} 
                />
                <span>I agree to the Terms and Conditions and Privacy Policy</span>
              </label>
            </div>
            
            <button 
              className={`consent-button ${!gdprChecked ? 'disabled' : ''}`}
              onClick={handleGdprConsent}
              disabled={!gdprChecked || isSubmittingConsent}
            >
              {isSubmittingConsent ? 'Submitting...' : 'Continue'}
            </button>
          </div>
        </div>
      )}
      
      {/* Regular team selection */}
      {teamChildCombos.length > 0 && (
        <>
          <h1>{isParent ? "Select Your Child's Team" : "Select Your Team"}</h1>
          
          {/* Team-Child Buttons */}
          <div className="team-grid">
            {teamChildCombos.map((combo, index) => (
              <div 
                key={`${combo.teamId}-${index}`} 
                className="team-button"
                onClick={() => handleTeamSwitch(
                  combo.teamId, 
                  combo.teamName, 
                  combo.teamType,
                  combo.childId,  // Pass child ID
                  combo.childName
                )}
              >
                <div className="team-logo">
                  <img 
                    src={`https://football.pebbledesigns.co.uk/api/images/teams/${combo.teamId}/logo-dark.png`}
                    alt={`${combo.teamName} logo`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {teamProStatus[combo.teamId] && (
                    <div className="pro-crown">
                      <FaCrown size={16} />
                    </div>
                  )}
                </div>
                <div className="team-info">
                  <span className="team-name">{combo.teamName}</span>
                  {combo.age_group && (
                    <span className="age-group">{combo.age_group}</span>
                  )}
                  {combo.childName && (
                    <span className="child-name">{combo.childName}</span>
                  )}
                </div>
                <div className="chevron-circle">
                  <FaChevronRight size={12} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      {/* SuperAdmin View All Teams section */}
      {isSuperAdmin && allTeams.length > 0 && (
        <>
          <h1 className="superadmin-heading">View Team as Player (SuperAdmin)</h1>
          <div className="team-grid superadmin-grid">
            {allTeams.map((team) => (
              <div 
                key={`admin-view-${team.team_id}`} 
                className="team-button superadmin-team-button"
                onClick={() => handleViewAsPlayer(
                  team.team_id, 
                  team.team_name, 
                  team.type,
                  team.admin_player_id
                )}
              >
                <div className="team-logo">
                  <img 
                    src={`https://football.pebbledesigns.co.uk/api/images/teams/${team.team_id}/logo-dark.png`}
                    alt={`${team.team_name} logo`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {teamProStatus[team.team_id] && (
                    <div className="pro-crown">
                      <FaCrown size={16} />
                    </div>
                  )}
                </div>
                <div className="team-info">
                  <span className="team-name">{team.team_name}</span>
                  <span className="team-type">{team.type} | Players: {team.team_length}</span>
                  {team.admin_name && (
                    <span className="team-admin">Viewing as: {team.admin_name}</span>
                  )}
                </div>
                <div className="view-icon">
                  <FaEye size={16} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      {/* Pending Teams */}
      {teams.some(team => team.status === 'pending') && (
        <div className="pending-section">
          <h2>Pending Approval</h2>
          <div className="team-grid">
            {teams.filter(team => team.status === 'pending').map((team) => {
              // Get children for this team
              const childrenForTeam = getChildrenForTeam(team.team_id);
              
              return (
                <div 
                  key={team.team_id} 
                  className="team-button disabled"
                >
                  <div className="team-logo">
                    <img 
                      src={`https://football.pebbledesigns.co.uk/api/images/teams/${team.team_id}/logo-dark.png`}
                      alt={`${team.team_name} logo`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {teamProStatus[team.team_id] && (
                      <div className="pro-crown">
                        <FaCrown size={16} />
                      </div>
                    )}
                  </div>
                  <div className="team-info">
                    <span className="team-name">{team.team_name}</span>
                    {childrenForTeam.length > 0 && (
                      <span className="child-name">{childrenForTeam[0].name}</span>
                    )}
                  </div>
                  <div className="chevron-circle">
                    <FaChevronRight size={12} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="pending-info">
            Your request is pending approval. If you have been waiting a while, please contact the team leader.
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamSelectionPage;